/* eslint-disable max-lines */
import convertPrice from '../convertPrice/convertPrice';
import {getProductType} from '@optimaxdev/analytics/mobile';
import {PRODUCT_TYPES} from '../../constants/product';
import {
    CheckoutContactsItemV2Type,
    CheckoutGlassesItemV2Type,
} from 'reducers/checkout/orderReview/orderReviewType';
import {QuoteItemBaseType, QuoteItemEyeglassesType} from '@typescript/cartTypes';
import {QuoteItemContactsType} from 'constants/flow/flowTypesCart';
import {ProductType} from '@page/wishlist/types';

export const PAGE_NAME_FOR_VIZURY = {
    HOME: 'home_page',
    CATEGORY: 'category_page',
    PRODUCT: 'product_page',
    CART: 'shopping_cart',
    TYP: 'thank_you',
} as const;

const arrayToObject = Object.values(PAGE_NAME_FOR_VIZURY);
type PagesLocationType = typeof arrayToObject[number];
type VizuryDataFromTYPType = {
    orderedItems: Array<CheckoutGlassesItemV2Type | CheckoutContactsItemV2Type>;
    id: string;
    revenue: string;
};

const VALUE_PAGE_FOR_VIZURY = {
    home_page: 'homepage',
    category_page: 'categorypage',
    product_page: 'productpage',
    shopping_cart: 'cartpage',
    thank_you: 'thankyoupage',
};

/**
 * Manual parsing data to vizury for data collection in spa mode
 * @param {string} page - page name
 */
export const manualParseDataForVizury = (page: string) => {
    const dataForParse = VALUE_PAGE_FOR_VIZURY[page];

    if (dataForParse && window.pixel) {
        window.pixel.parse(dataForParse);
    }
};

/**
 * Set data to vizLayer on home page
 */
export const vizuryForHP = () => {
    window.vizLayer = {
        type: PAGE_NAME_FOR_VIZURY.HOME,
    };
};

/**
 * Set data to vizLayer on category page
 * @param {string} categoryName - name of current category
 */
export const vizuryForCategory = (categoryName: string) => {
    window.vizLayer = {
        type: PAGE_NAME_FOR_VIZURY.CATEGORY,
        catid: categoryName,
    };
};

/**
 * Set data to vizLayer on product page
 * @param {SimpleProductType} product - product data
 */
export const vizuryForPDP = (product: ProductType) => {
    window.vizLayer = {
        type: PAGE_NAME_FOR_VIZURY.PRODUCT,
        pid: product.sku,
        pname: product.parentName || '',
        lp: window.location.href,
        availability: product.stock.status || 'in_stock',
        currency: 'USD',
        newp: convertPrice(product.price),
        old: convertPrice(product.price),
    };
};

/**
 * Set data to vizLayer on cart page
 * @param {SimpleProductType} quoteItems - quoteItems data
 * @param {string} cartKey - cart key
 */
export const vizuryForCart = (
    quoteItems: QuoteItemEyeglassesType[] | QuoteItemContactsType[],
    cartKey: string,
) => {
    const quoteItemsData = {};
    let grandTotalInCart = 0;

    if (quoteItems.length) {
        // eslint-disable-next-line max-statements
        quoteItems.forEach((item, index) => {
            const isContact = item.type === PRODUCT_TYPES.CONTACT_LENSES;
            grandTotalInCart += item.totalFinalPrice;
            quoteItemsData[`ptitle${index}`] = item.name;
            quoteItemsData[`price${index}`] = item.totalFinalPrice;
            quoteItemsData[`pbrand${index}`] = item.brand;
            quoteItemsData[`pid${index}`] = item.product_sku;
            quoteItemsData[`psku${index}`] = item.sku;
            quoteItemsData[`psize${index}`] = isContact ? '' : item.frame.frameSize;
            quoteItemsData[`pcolor${index}`] = isContact ? '' : item.frame.color;
            quoteItemsData[`pgender${index}`] = isContact ? '' : item.gender?.join(', ');
            quoteItemsData[`ptype${index}`] = item.type;
            quoteItemsData[`pbadge${index}`] = item.badges?.length ? item.badges[0].title : '';
            quoteItemsData[`catid${index}`] = isContact ? '' : item.glasses_type;
            quoteItemsData[`pimg${index}`] = item.img;
            quoteItemsData[`purl${index}`] = `${window.location.origin}/${item.frame_url_path}`;
            quoteItemsData[`quantity${index}`] = item.qty;
        });
    }

    window.vizLayer = {
        type: PAGE_NAME_FOR_VIZURY.CART,
        currency: 'USD',
        carturl: `https://www.glassesusa.com/checkout/cart?${cartKey}`,
        cartval: grandTotalInCart,
        ...quoteItemsData,
    };
};

/**
 * Set data to vizLayer on TYP
 * @param {VizuryDataFromTYPType} dataForTYP - quoteItems data
 * @param {string} cartKey - cart key
 */
export const vizuryForTYP = ({orderedItems, id, revenue}: VizuryDataFromTYPType) => {
    const additionalDataForTYP = {};

    if (orderedItems.length) {
        orderedItems.forEach((item, index) => {
            additionalDataForTYP[`price${index}`] =
                item[
                    item.product.type === PRODUCT_TYPES.CONTACT_LENSES
                        ? 'discountedPrice'
                        : 'totalFinalPrice'
                ];
            additionalDataForTYP[`pid${index}`] = item.product.sku;
            additionalDataForTYP[`catid${index}`] = getProductType(item.product.type);
            additionalDataForTYP[`quantity${index}`] = item.qty;
        });
    }

    window.vizLayer = {
        type: PAGE_NAME_FOR_VIZURY.TYP,
        currency: 'USD',
        isvizconv: 0,
        orderid: id,
        orderprice: revenue,
        ...additionalDataForTYP,
    };
};

/**
 * Call necessary function which depends on current page
 * @param {string} location - call`s location
 * @param {string | SimpleProductType | QuoteItemBaseType[] | VizuryDataFromTYPType} data - data for push to vizury
 * @param {string} cartKey - cart key
 */
export const pushVizury = (
    location: PagesLocationType,
    data?: string | ProductType | QuoteItemBaseType[] | VizuryDataFromTYPType,
    cartKey = '',
) => {
    window.vizLayer = {};

    switch (location) {
        case PAGE_NAME_FOR_VIZURY.HOME:
            vizuryForHP();
            break;
        case PAGE_NAME_FOR_VIZURY.CATEGORY:
            vizuryForCategory(data as string);
            break;
        case PAGE_NAME_FOR_VIZURY.PRODUCT:
            vizuryForPDP(data as ProductType);
            break;
        case PAGE_NAME_FOR_VIZURY.CART:
            vizuryForCart(data as QuoteItemEyeglassesType[] | QuoteItemContactsType[], cartKey);
            break;
        case PAGE_NAME_FOR_VIZURY.TYP:
            vizuryForTYP(data as VizuryDataFromTYPType);
            break;
        default:
            return;
    }

    if (window.pixel) manualParseDataForVizury(location);
};
